import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

interface SeoSetOptions {
  title: string;
  description?: string;
  path?: string;
  image?: string;
  robots?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly siteName = '마이스좋은공간';
  private readonly brandName = '박람회전문기업';
  private readonly siteUrl = 'https://bestexpo.kr';
  readonly defaultTitle = `${this.siteName} | ${this.brandName}`;
  readonly defaultDescription =
    '새로운 가치를 창조하는 박람회 전문기업 마이스좋은공간입니다. 차별화된 전시와 박람회를 만나보세요.';
  private readonly defaultImage = `${this.siteUrl}/assets/og-image.png`;

  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);
  private readonly document = inject(DOCUMENT);

  set({
    title,
    description = this.defaultDescription,
    path,
    image = this.defaultImage,
    robots = 'index, follow',
  }: SeoSetOptions): void {
    const pageTitle = `${title} | ${this.siteName}`;
    const canonicalUrl = new URL(path || this.document.location.pathname, this.siteUrl).toString();

    this.applyMeta({
      title: pageTitle,
      description,
      robots,
      canonicalUrl,
      image,
    });
  }

  setDefault(): void {
    this.applyMeta({
      title: this.defaultTitle,
      description: this.defaultDescription,
      robots: 'index, follow',
      canonicalUrl: this.siteUrl,
      image: this.defaultImage,
    });
  }

  private updateCanonicalLink(url: string): void {
    let link = this.document.head.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  private applyMeta({
    title,
    description,
    robots,
    canonicalUrl,
    image,
  }: {
    title: string;
    description: string;
    robots: string;
    canonicalUrl: string;
    image: string;
  }): void {
    this.titleService.setTitle(title);

    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'robots', content: robots });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({
      property: 'og:description',
      content: description,
    });
    this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
    this.metaService.updateTag({ property: 'og:image', content: image });

    this.updateCanonicalLink(canonicalUrl);
  }
}
